import { lazy, Suspense, useEffect, useRef } from "react";
import {
  ClerkLoaded,
  ClerkLoading,
  ClerkProvider,
  SignIn,
  SignUp,
  Show,
  useAuth,
  useClerk,
  useUser,
} from "@clerk/react";
import { publishableKeyFromHost } from "@clerk/react/internal";
import {
  Switch,
  Route,
  useLocation,
  Router as WouterRouter,
  Redirect,
} from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";
import { PhotoUploadQueueProvider } from "@workspace/object-storage-web";

import NotFound from "@/pages/not-found";
import { Landing } from "@/pages/landing";
import { BackupDownloadProvider } from "@/components/backup-download-provider";
import { AccountAccessGate } from "@/components/account-access-gate";

const Collection = lazy(() =>
  import("@/pages/collection").then(({ Collection }) => ({
    default: Collection,
  })),
);
const CollectionE2EHarness = lazy(() =>
  import("@/pages/collection").then(({ CollectionE2EHarness }) => ({
    default: CollectionE2EHarness,
  })),
);
const ItemDetail = lazy(() =>
  import("@/pages/item-detail").then(({ ItemDetail }) => ({
    default: ItemDetail,
  })),
);
const ItemDetailE2EHarness = lazy(() =>
  import("@/pages/item-detail").then(({ ItemDetailE2EHarness }) => ({
    default: ItemDetailE2EHarness,
  })),
);
const Capture = lazy(() =>
  import("@/pages/capture").then(({ Capture }) => ({
    default: Capture,
  })),
);
const CaptureE2EHarness = lazy(() =>
  import("@/pages/capture").then(({ CaptureE2EHarness }) => ({
    default: CaptureE2EHarness,
  })),
);
const CaptureSetE2EHarness = lazy(() =>
  import("@/pages/capture-set").then(({ CaptureSetE2EHarness }) => ({
    default: CaptureSetE2EHarness,
  })),
);
const Search = lazy(() =>
  import("@/pages/search").then(({ Search }) => ({ default: Search })),
);
const Account = lazy(() =>
  import("@/pages/account").then(({ Account }) => ({ default: Account })),
);
const UserGuide = lazy(() =>
  import("@/pages/guide/index").then((m) => ({ default: m.UserGuide })),
);
const UserGuideArticle = lazy(() =>
  import("@/pages/guide/article").then((m) => ({ default: m.UserGuideArticle })),
);
const ForgeNotesList = lazy(() =>
  import("@/pages/forge-notes/index").then((m) => ({ default: m.ForgeNotesList }))
);
const ForgeNoteForm = lazy(() =>
  import("@/pages/forge-notes/forge-note-form").then((m) => ({ default: m.ForgeNoteForm }))
);
const PublicForgeNote = lazy(() =>
  import("@/pages/public/forge-note").then((m) => ({ default: m.PublicForgeNote }))
);

const clerkPubKey = publishableKeyFromHost(
  window.location.hostname,
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);

const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
const captureE2EHarnessEnabled =
  import.meta.env.DEV && import.meta.env.VITE_E2E_CAPTURE_HARNESS === "1";

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY in .env file");
}

const clerkLocalization = {
  signIn: { start: { title: "Sign in to ForgeBook" } },
  signUp: { start: { title: "Create your ForgeBook account" } },
};

const clerkAppearance = {
  cssLayerName: "clerk",
  variables: {
    colorPrimary: "hsl(23, 48%, 44%)",
    colorForeground: "hsl(36, 13%, 12%)",
    colorBackground: "hsl(42, 33%, 95%)",
    fontFamily: "'Inter', sans-serif",
    borderRadius: "0.25rem",
  },
};

function SignInPage() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <SignIn
        routing="path"
        path={`${basePath}/sign-in`}
        signUpUrl={`${basePath}/sign-up`}
      />
    </div>
  );
}

function SignUpPage() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <SignUp
        routing="path"
        path={`${basePath}/sign-up`}
        signInUrl={`${basePath}/sign-in`}
      />
    </div>
  );
}

function HomeRedirect() {
  const { isLoaded, isSignedIn } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      setLocation("/collection", { replace: true });
    }
  }, [isLoaded, isSignedIn, setLocation]);

  if (!isLoaded || isSignedIn) {
    return <RouteLoadingFallback />;
  }

  return <Landing />;
}

function RouteLoadingFallback() {
  return (
    <div
      className="flex min-h-[100dvh] items-center justify-center bg-background"
      role="status"
      aria-label="Loading page"
    >
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function ProtectedRoute({
  component: Component,
}: {
  component: React.ComponentType;
}) {
  return (
    <>
      <Show when="signed-in">
        <AccountAccessGate>
          <BackupDownloadProvider>
            <Component />
          </BackupDownloadProvider>
        </AccountAccessGate>
      </Show>
      <Show when="signed-out">
        <Redirect to="/" />
      </Show>
    </>
  );
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (
        prevUserIdRef.current !== undefined &&
        prevUserIdRef.current !== userId
      ) {
        queryClient.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, queryClient]);

  return null;
}

function AuthenticatedPhotoQueue({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  return (
    <PhotoUploadQueueProvider userId={user?.id}>{children}</PhotoUploadQueueProvider>
  );
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      localization={clerkLocalization}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <ClerkLoading>
        <RouteLoadingFallback />
      </ClerkLoading>
      <ClerkLoaded>
        <QueryClientProvider client={queryClient}>
          <ClerkQueryClientCacheInvalidator />
          <AuthenticatedPhotoQueue>
            <TooltipProvider>
              <Suspense fallback={<RouteLoadingFallback />}>
                <Switch>
                <Route path="/" component={HomeRedirect} />
                <Route path="/sign-in/*?" component={SignInPage} />
                <Route path="/sign-up/*?" component={SignUpPage} />
                <Route path="/note/:token" component={PublicForgeNote} />
                <Route
                  path="/collection"
                  component={() => <ProtectedRoute component={Collection} />}
                />
                <Route
                  path="/forge-notes"
                  component={() => <ProtectedRoute component={ForgeNotesList} />}
                />
                <Route
                  path="/forge-notes/:noteId/edit"
                  component={() => <ProtectedRoute component={ForgeNoteForm} />}
                />
                <Route
                  path="/item/:id/forge-note"
                  component={() => <ProtectedRoute component={ForgeNoteForm} />}
                />
                <Route
                  path="/search"
                  component={() => <ProtectedRoute component={Search} />}
                />
                <Route
                  path="/account"
                  component={() => <ProtectedRoute component={Account} />}
                />
                <Route
                  path="/guide"
                  component={() => <ProtectedRoute component={UserGuide} />}
                />
                <Route
                  path="/guide/:slug"
                  component={() => <ProtectedRoute component={UserGuideArticle} />}
                />
                <Route
                  path="/item/:id"
                  component={() => <ProtectedRoute component={ItemDetail} />}
                />
                <Route
                  path="/item/:id/edit"
                  component={() => <ProtectedRoute component={Capture} />}
                />
                <Route
                  path="/capture"
                  component={() => <ProtectedRoute component={Capture} />}
                />
                <Route component={NotFound} />
                </Switch>
              </Suspense>
              <Toaster />
              <SonnerToaster />
            </TooltipProvider>
          </AuthenticatedPhotoQueue>
        </QueryClientProvider>
      </ClerkLoaded>
    </ClerkProvider>
  );
}

function CaptureE2EHarnessApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <PhotoUploadQueueProvider userId="capture-e2e-user">
        <TooltipProvider>
          <BackupDownloadProvider>
            <Suspense fallback={<RouteLoadingFallback />}>
              <Switch>
              <Route path="/item/:id/edit" component={CaptureE2EHarness} />
              <Route path="/item/:id/forge-note" component={ForgeNoteForm} />
              <Route path="/forge-notes/:noteId/edit" component={ForgeNoteForm} />
              <Route path="/note/:token" component={PublicForgeNote} />
              <Route path="/item/:id" component={ItemDetailE2EHarness} />
              <Route path="/capture" component={CaptureE2EHarness} />
              <Route path="/sets/new" component={CaptureSetE2EHarness} />
              <Route path="/collection" component={CollectionE2EHarness} />
              <Route path="/guide" component={UserGuide} />
              <Route path="/guide/:slug" component={UserGuideArticle} />
              <Route component={NotFound} />
              </Switch>
            </Suspense>
          </BackupDownloadProvider>
          <Toaster />
          <SonnerToaster />
        </TooltipProvider>
      </PhotoUploadQueueProvider>
    </QueryClientProvider>
  );
}

function App() {
  return (
    <WouterRouter base={basePath}>
      {captureE2EHarnessEnabled ? (
        <CaptureE2EHarnessApp />
      ) : (
        <ClerkProviderWithRoutes />
      )}
    </WouterRouter>
  );
}

export default App;
